import React, { HTMLAttributes, useCallback, useContext } from 'react'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'

interface Props extends HTMLAttributes<HTMLButtonElement> {
  airport?: App.Airport
  offer?: App.Offer
  activeFilter?: string
  offerType: App.LEContractedAnalyticsType
  inSidebar?: boolean
  iconDisabled?: boolean
  ref?: React.Ref<HTMLButtonElement>
  charterBundleRoute?: App.CharterBundleRoute
  disabled?: boolean
}

function OfferAirportSelectBox(props: Props) {
  const {
    airport,
    activeFilter,
    offerType,
    offer,
    iconDisabled,
    charterBundleRoute,
    disabled: propDisabled,
    ...rest
  } = props

  const dispatch = useOfferPageDispatch()
  const isOpen = activeFilter === 'airport'
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const onBoxClick = useCallback(() => {
    if (propDisabled || (isAgentHubEnabled && charterBundleRoute)) {
      // Prevent interaction when disabled or when agentHub is enabled for charter flights
      return
    }
    if (isOpen) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('AIRPORT_FILTER_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
      trackEvent('AIRPORT_FILTER_PRESSED')
    }
  }, [isOpen, dispatch, trackEvent, charterBundleRoute, propDisabled])

  const isAirportRequired = !!(offer?.bundledWithFlightsOnly || charterBundleRoute)
  const isDisabled = propDisabled || (isAgentHubEnabled && !!charterBundleRoute)

  return (<OfferFilterDropdownBox
    {...rest}
    data-testid="origin-select"
    onClick={isDisabled ? undefined : onBoxClick}
    active={isOpen}
    icon={!iconDisabled && <LinePlaneDepartureIcon />}
    label={`Departure airport ${isAirportRequired ? '' : '(optional)'}`}
    value={airport ? <b>{airport.name} ({airport.code.toUpperCase()})</b> : undefined}
    placeholder="Select by airport"
  />
  )
}

OfferAirportSelectBox.displayName = 'OfferAirportSelectBox'

export default withOfferPageState((state) => ({
  airport: state.airport,
  activeFilter: state.activeFilter,
  offer: state.offer,
  offerType: state.offer?.analyticsType || state.bedbankOffer?.analyticsType,
  charterBundleRoute: state.charterBundleRoute,
}), OfferAirportSelectBox)
